function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { EffectiveDateSource } from "../../../../../models/service/model";
import { fromDateStampObject } from "../../../../../models/time/translator";
import { BillingScheduleGroupType } from "../../../../../types/api/relationship/billingSchedule.model";
import { formatLongMonthDateWithLocalTimezone } from "../../../../../utils/date-formatting";
const formatServiceNotes = function formatServiceNotes(service) {
  let context = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const notes = [];

  // Note: priorPeriods is now handled separately, not in notes

  if (service.isProrated) {
    if (context.isToday) {
      notes.push('First payment is prorated');
    } else if (context.isEffectiveDate) {
      notes.push("First prorated payment occurs on the agreement's effective date");
    } else {
      notes.push('First payment is prorated');
    }
  }
  if (service.billingStages) {
    notes.push("Stage ".concat(service.billingStages.stageNumber, "/").concat(service.billingStages.totalStages));
  }
  if (context.isToday && !service.isProrated && !service.priorPeriods && !service.billingStages) {
    if (service.effectiveDateSource === EffectiveDateSource.System) {
      notes.push('First payment occurs today');
    }
  }
  if (context.isEffectiveDate && !service.isProrated && !service.priorPeriods && !service.billingStages) {
    notes.push("First payment occurs on the agreement's effective date");
  }
  return notes;
};
const translateService = function translateService(service) {
  let context = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return {
    serviceId: service.serviceId,
    name: service.name,
    price: service.price / 100,
    priorPeriods: context.isToday && service.priorPeriods && service.priorPeriods > 0 && service.priorPeriodsTotal ? {
      count: service.priorPeriods,
      totalAmount: service.priorPeriodsTotal / 100
    } : undefined,
    notes: formatServiceNotes(service, context)
  };
};
const formatFrequencyLabel = type => {
  switch (type) {
    case BillingScheduleGroupType.Weekly:
      return 'weekly';
    case BillingScheduleGroupType.Biweekly:
      return 'every two weeks';
    case BillingScheduleGroupType.Monthly:
      return 'monthly';
    case BillingScheduleGroupType.Quarterly:
      return 'quarterly';
    case BillingScheduleGroupType.Yearly:
      return 'yearly';
    default:
      return '';
  }
};
const formatDayOfMonth = dayOfMonth => {
  if (!dayOfMonth) return '';
  if (dayOfMonth.type === 'lastDayOfMonth') {
    return 'on the last day of the month';
  }
  if (dayOfMonth.dayOfMonth) {
    const day = dayOfMonth.dayOfMonth.day;
    if (day === 1) return 'on the 1st';
    if (day === 2) return 'on the 2nd';
    if (day === 3) return 'on the 3rd';
    if (day >= 4 && day <= 20) return "on the ".concat(day, "th");
    if (day === 21) return 'on the 21st';
    if (day === 22) return 'on the 22nd';
    if (day === 23) return 'on the 23rd';
    if (day >= 24 && day <= 30) return "on the ".concat(day, "th");
    if (day === 31) return 'on the 31st';
    return "on the ".concat(day);
  }
  return '';
};
const formatQuarterlyDay = (billingOnDay, lastDayOfPeriod) => {
  if (lastDayOfPeriod) {
    return 'on the last day of the quarter';
  }
  if (billingOnDay === 0) {
    return 'on the first day of the quarter';
  }
  return "on day ".concat(billingOnDay, " of the quarter");
};
export const translateBillingSchedule = function translateBillingSchedule(billingSchedule) {
  let today = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : new Date();
  const sections = [];
  for (const group of billingSchedule.groups) {
    switch (group.type) {
      case BillingScheduleGroupType.Today:
        {
          const services = [];
          for (const service of group.today.services) {
            const translatedService = translateService(service, {
              isToday: true
            });

            // If service has prior periods, duplicate it: one for prior periods, one for today
            if (translatedService.priorPeriods) {
              // First entry: prior periods
              const priorPeriodsCount = translatedService.priorPeriods.count;
              services.push(_objectSpread(_objectSpread({}, translatedService), {}, {
                price: translatedService.priorPeriods.totalAmount,
                notes: ["Includes ".concat(priorPeriodsCount, " prior ").concat(priorPeriodsCount === 1 ? 'period' : 'periods')],
                isPriorPeriodsEntry: true
              }));

              // Second entry: today's payment - recalculate notes without prior periods constraint
              // Create a modified service object without priorPeriods for note calculation
              const serviceForNotes = _objectSpread(_objectSpread({}, service), {}, {
                priorPeriods: undefined,
                priorPeriodsTotal: undefined
              });
              const todayService = _objectSpread(_objectSpread({}, translatedService), {}, {
                priorPeriods: undefined,
                // Remove prior periods from today entry
                isPriorPeriodsEntry: false,
                notes: formatServiceNotes(serviceForNotes, {
                  isToday: true
                }) // Recalculate notes
              });
              services.push(todayService);
            } else {
              services.push(translatedService);
            }
          }
          sections.push({
            title: 'Billed today',
            totalAmount: group.today.totalAmount / 100,
            services
          });
          break;
        }
      case BillingScheduleGroupType.OnRelationshipEffectiveDate:
        {
          const effectiveDate = fromDateStampObject(group.onRelationshipEffectiveDate.effectiveDate);
          const formattedDate = formatLongMonthDateWithLocalTimezone(effectiveDate);
          const isFuture = effectiveDate > today;
          if (isFuture) {
            const services = group.onRelationshipEffectiveDate.services.map(service => translateService(service, {
              isEffectiveDate: true
            }));
            sections.push({
              title: "Billed on ".concat(formattedDate),
              totalAmount: group.onRelationshipEffectiveDate.totalAmount / 100,
              services
            });
          }
          break;
        }
      case BillingScheduleGroupType.BilledOnStages:
        {
          const services = group.billedOnStages.services.map(service => translateService(service));
          sections.push({
            title: 'Billed in stages',
            totalAmount: group.billedOnStages.totalAmount / 100,
            services
          });
          break;
        }
      case BillingScheduleGroupType.Weekly:
        {
          const frequency = formatFrequencyLabel(group.type);
          const dayOfWeek = group.weekly.billingOnDayOfWeek;
          const effectiveDate = fromDateStampObject(group.weekly.effectiveDate);
          const formattedDate = formatLongMonthDateWithLocalTimezone(effectiveDate);
          const isToday = effectiveDate <= today;
          const services = group.weekly.services.map(service => translateService(service));
          const title = isToday ? "Billed ".concat(frequency, " on ").concat(dayOfWeek) : "Billed ".concat(frequency, " on ").concat(dayOfWeek, ", starting on ").concat(formattedDate);
          sections.push({
            title,
            totalAmount: group.weekly.totalAmount / 100,
            services
          });
          break;
        }
      case BillingScheduleGroupType.Biweekly:
        {
          const frequency = formatFrequencyLabel(group.type);
          const dayOfWeek = group.biweekly.billingOnDayOfWeek;
          const effectiveDate = fromDateStampObject(group.biweekly.effectiveDate);
          const formattedDate = formatLongMonthDateWithLocalTimezone(effectiveDate);
          const isToday = effectiveDate <= today;
          const services = group.biweekly.services.map(service => translateService(service));
          const title = isToday ? "Billed ".concat(frequency, " on ").concat(dayOfWeek) : "Billed ".concat(frequency, " on ").concat(dayOfWeek, ", starting on ").concat(formattedDate);
          sections.push({
            title,
            totalAmount: group.biweekly.totalAmount / 100,
            services
          });
          break;
        }
      case BillingScheduleGroupType.Monthly:
        {
          const frequency = formatFrequencyLabel(group.type);
          const dayOfMonthStr = formatDayOfMonth(group.monthly.dayOfMonth);
          const effectiveDate = fromDateStampObject(group.monthly.effectiveDate);
          const formattedDate = formatLongMonthDateWithLocalTimezone(effectiveDate);
          const isToday = effectiveDate <= today;
          const services = group.monthly.services.map(service => translateService(service));
          const title = isToday ? "Billed ".concat(frequency, " ").concat(dayOfMonthStr) : "Billed ".concat(frequency, " ").concat(dayOfMonthStr, ", starting on ").concat(formattedDate);
          sections.push({
            title: title.trim(),
            totalAmount: group.monthly.totalAmount / 100,
            services
          });
          break;
        }
      case BillingScheduleGroupType.Quarterly:
        {
          const frequency = formatFrequencyLabel(group.type);
          const dayStr = formatQuarterlyDay(group.quarterly.billingOnDay, group.quarterly.lastDayOfPeriod);
          const effectiveDate = fromDateStampObject(group.quarterly.effectiveDate);
          const formattedDate = formatLongMonthDateWithLocalTimezone(effectiveDate);
          const isToday = effectiveDate <= today;
          const services = group.quarterly.services.map(service => translateService(service));
          const title = isToday ? "Billed ".concat(frequency, " ").concat(dayStr) : "Billed ".concat(frequency, " ").concat(dayStr, ", starting on ").concat(formattedDate);
          sections.push({
            title: title.trim(),
            totalAmount: group.quarterly.totalAmount / 100,
            services
          });
          break;
        }
      case BillingScheduleGroupType.Yearly:
        {
          const frequency = formatFrequencyLabel(group.type);
          const effectiveDate = fromDateStampObject(group.yearly.effectiveDate);
          const formattedDate = formatLongMonthDateWithLocalTimezone(effectiveDate);
          const isToday = effectiveDate <= today;
          const services = group.yearly.services.map(service => translateService(service));
          const title = isToday ? "Billed ".concat(frequency) : "Billed ".concat(frequency, ", starting on ").concat(formattedDate);
          sections.push({
            title: title.trim(),
            totalAmount: group.yearly.totalAmount / 100,
            services
          });
          break;
        }
      case BillingScheduleGroupType.Paused:
        {
          const services = group.paused.services.map(service => translateService(service));
          sections.push({
            title: 'Billed monthly upon activation',
            totalAmount: group.paused.totalAmount / 100,
            services
          });
          break;
        }
    }
  }
  const combinedSections = new Map();
  for (const section of sections) {
    const existing = combinedSections.get(section.title);
    if (existing) {
      existing.totalAmount += section.totalAmount;
      existing.services.push(...section.services);
    } else {
      combinedSections.set(section.title, _objectSpread({}, section));
    }
  }
  return {
    sections: Array.from(combinedSections.values())
  };
};